import { defineStore } from 'pinia'
import { getCategories, getProductsByCategory } from '@/api/product'
import { getHotIndustries } from '@/api/solution'
import { getActiveNavbar } from '@/api/navbar'
import {
  Guide as RouterIcon, Cpu, Box, Grid, Share, Cloudy,
  Monitor as MonitorIcon, EditPen, Connection
} from '@element-plus/icons-vue'

// 六个主分类配置（与原组件一致）
const MAIN_CATEGORIES = [
  { title: '网络基础设施', codes: ['router', 'switch', 'ap', 'module', 'antenna'] },
  { title: '工业设备联网', codes: ['dtu', 'gateway', 'converter'] },
  { title: '工业边缘控制', codes: ['edge-computing'] },
  { title: '智能感知设备', codes: ['sensor'] },
  { title: '软件和服务', codes: ['software'] },
  { title: '行业产品及定制', codes: ['power-supply'] }
]

// 图标映射（仅用于产品分类图标）
const ICON_MAP = {
  router: RouterIcon, dtu: Box, gateway: Cpu, module: Box,
  switch: Grid, ap: Share, software: Cloudy,
  ipc: MonitorIcon, arm: Cpu, hmi: EditPen, default: Connection
}
const getIconComponent = (code) => ICON_MAP[code] || ICON_MAP.default

export const useProductNavStore = defineStore('productNav', {
  state: () => ({
    categories: [],       // 产品导航数据
    rawIndustries: [],    // 从接口获取的行业原始列表（不含展示字段）
    navbarItems: [],      // 自定义导航栏项
    lastFetch: 0,         // 最后成功请求时间戳
    loading: false        // 数据是否正在加载
  }),

  actions: {
    async fetchProductNav(maxAge = 5 * 60 * 1000) {
      const now = Date.now()
      // 缓存有效且数据非空时，直接跳过
      if (now - this.lastFetch < maxAge && this.categories.length > 0) return
      // 防止并发重复请求
      if (this.loading) return

      this.loading = true
      try {
        // ========= 1. 获取产品分类及产品列表 =========
        const catRes = await getCategories()
        if (catRes.code !== 200) throw new Error(catRes.message)

        const categoryMap = {}
        catRes.data.forEach(cat => {
          categoryMap[cat.code] = {
            id: cat.id,
            name: cat.name,
            code: cat.code,
            icon: getIconComponent(cat.icon)
          }
        })

        const fetchPromises = MAIN_CATEGORIES.map(async (mainCat) => {
          const items = []
          const seenIds = new Set()
          for (const code of mainCat.codes) {
            if (!categoryMap[code]) continue
            try {
              const prodRes = await getProductsByCategory(code, 1, 5)
              if (prodRes.code === 200 && prodRes.data?.list) {
                prodRes.data.list.forEach(p => {
                  if (seenIds.has(p.id)) return
                  seenIds.add(p.id)
                  items.push({
                    id: p.id,
                    name: p.name,
                    model: p.model || p.name,
                    description: p.description || p.desc || '',
                    icon: categoryMap[code].icon,
                    tag: p.tag || null,
                    tagType: p.tag === 'new' ? 'danger' : p.tag === 'recommend' ? 'success' : 'warning',
                    image: p.image || `https://picsum.photos/seed/${p.id}/400/280`,
                    categoryCode: code
                  })
                })
              }
            } catch (e) {
              console.warn(`获取分类 ${code} 产品失败:`, e)
            }
          }
          return { title: mainCat.title, items: items.slice(0, 5) }
        })

        this.categories = await Promise.all(fetchPromises)

        // ========= 2. 获取热门行业（仅存原始数据） =========
        try {
          const hotRes = await getHotIndustries(5)
          if (hotRes.code === 200) {
            this.rawIndustries = hotRes.data // 只保存接口返回的原始数组
          }
        } catch (e) {
          console.error('获取热门行业失败:', e)
          // 允许部分失败，不影响产品导航
        }

        // ========= 3. ??????? =========
        try {
          const navRes = await getActiveNavbar()
          if (navRes.code === 200) {
            this.navbarItems = navRes.data || []
          }
        } catch (e) {
          console.error('?????????:', e)
        }

        this.lastFetch = now
      } catch (error) {
        console.error('加载导航数据失败:', error)
        throw error
      } finally {
        this.loading = false
      }
    }
  }
})
